import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ChevronLeft, Wind } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { API } from "@/lib/api";
import { toast } from "sonner";

const RECOMMENDED_EXERCISES = {
  "1-1": { id: "se-dina-monster", title: "Se dina mönster", why: "Denna övning hjälper dig fördjupa stegets tema — att stanna upp och börja observera det som sker inom dig." },
  "1-2": { id: "triggerpunkten", title: "Triggerpunkten", why: "En djupdykning i vad som utlöser dina starkaste reaktioner — och var de egentligen kommer ifrån." },
  "1-3": { id: "inre-dialog", title: "Möt din inre kritiker", why: "Utforska din inre röst och börja skilja på kritikerns ord och din egen sanning." },
  "2-1": { id: "skammens-anatomi", title: "Skammens anatomi", why: "Möt den skam du bär på och börja skilja den från sanningen om dig." },
  "2-2": { id: "slappa-overansvar", title: "Släppa överansvar", why: "En guidad visualisering som hjälper dig att lägga ner det ansvar som inte längre är ditt att bära." },
  "2-3": { id: "vara-utan-att-prestera", title: "Vara utan att prestera", why: "En övning i att låta dig finnas i tomrummet — utan krav på att göra, prestera eller fylla det." },
  "3-1": { id: "granser-inifran", title: "Sätta gränser inifrån", why: "Känn efter var dina gränser går och ge dig själv tillåtelse att välja annorlunda." },
  "3-2": { id: "andas-och-landa", title: "Andas och landa", why: "Använd andningen som ankare för att komma tillbaka till dig själv och den trygghet du bygger inifrån." },
  "3-3": { id: "forankra-i-vardagen", title: "Förankra förändringen", why: "Integrera det du lärt dig om dig själv och gör det till en del av din vardag." },
};

export default function LayerDetailPage() {
  const { id } = useParams();
  const [layer, setLayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reflections, setReflections] = useState({});
  const [savingIdx, setSavingIdx] = useState(null);
  const [completing, setCompleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLayer = async () => {
      try {
        const { data } = await API.get(`/journey/layer/${id}`);
        setLayer(data);
        const saved = {};
        if (data.saved_reflections) {
          Object.entries(data.saved_reflections).forEach(([idx, val]) => {
            saved[parseInt(idx)] = val.content;
          });
        }
        setReflections(saved);
      } catch (err) {
        if (err.response?.status === 403) {
          toast.error("Du behöver slutföra tidigare steg först");
        }
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchLayer();
  }, [id, navigate]);

  const saveReflection = async (questionIndex) => {
    const content = reflections[questionIndex];
    if (!content?.trim()) return;
    setSavingIdx(questionIndex);
    try {
      await API.post("/journey/reflect", {
        layer_id: id,
        question_index: questionIndex,
        content: content.trim()
      });
      toast.success("Reflektion sparad");
    } catch {
      toast.error("Kunde inte spara");
    } finally {
      setSavingIdx(null);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const { data } = await API.post("/journey/complete", { layer_id: id });
      toast.success("Steg slutfört!");
      if (data.phase_completed) {
        navigate(`/fas-avslutning/${data.phase_completed}`);
      } else if (data.next_layer) {
        navigate(`/steg/${data.next_layer}`);
      } else {
        navigate('/avslutning');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Något gick fel");
    } finally {
      setCompleting(false);
    }
  };

  const ALL_LAYERS = [
    "1-1", "1-2", "1-3", "2-1", "2-2", "2-3", "3-1", "3-2", "3-3"
  ];
  const LAYER_TITLES = {
    "1-1": "Att stanna upp", "1-2": "Dina reaktioner och mönster", "1-3": "Din inre röst",
    "2-1": "Känslor du bär på", "2-2": "Att lägga ner det som inte är ditt", "2-3": "Vem är du utan det gamla?",
    "3-1": "Att börja välja annorlunda", "3-2": "Gränser, trygghet och självmedkänsla", "3-3": "Att leva fritt"
  };
  const PHASE_NAMES = { "1": "Klarhet", "2": "Frigörelse", "3": "Förankring" };
  const currentIdx = ALL_LAYERS.indexOf(id);
  const prevLayer = currentIdx > 0 ? ALL_LAYERS[currentIdx - 1] : null;
  const nextLayer = currentIdx < ALL_LAYERS.length - 1 ? ALL_LAYERS[currentIdx + 1] : null;

  if (loading || !layer) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#A0655A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <div className="pt-20 sm:pt-24 pb-8 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          
          {/* Step navigation bar */}
          <div className="mb-8" data-testid="step-navigator">
            <div className="flex items-center justify-center gap-1 mb-2">
              {ALL_LAYERS.map((lid, i) => {
                const isActive = lid === id;
                const phaseNum = lid.split('-')[0];
                const showDivider = i > 0 && lid.split('-')[0] !== ALL_LAYERS[i-1].split('-')[0];
                return (
                  <div key={lid} className="flex items-center">
                    {showDivider && <div className="w-3 sm:w-4" />}
                    <button
                      onClick={() => navigate(`/steg/${lid}`)}
                      title={LAYER_TITLES[lid]}
                      className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full text-xs font-medium transition-all flex items-center justify-center ${
                        isActive
                          ? 'bg-[#A0655A] text-white scale-110'
                          : layer.completed_layers?.includes(lid)
                          ? 'bg-[#A0655A]/20 text-[#A0655A] hover:bg-[#A0655A]/30'
                          : 'bg-[#E8E0DB]/50 text-[#999] hover:bg-[#E8E0DB]'
                      }`}
                      data-testid={`step-dot-${lid}`}
                    >
                      {i + 1}
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center gap-8 sm:gap-16">
              {["1", "2", "3"].map(p => (
                <span key={p} className={`text-[10px] uppercase tracking-wider ${
                  id.startsWith(p) ? 'text-[#A0655A] font-medium' : 'text-[#999]'
                }`}>{PHASE_NAMES[p]}</span>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <p className="text-sm text-[#A0655A] font-medium uppercase tracking-wide mb-2">
              {layer.movement_title} · {['Att se', 'Att släppa', 'Att vara'][layer.movement_id - 1]}
            </p>
            <h1 className="heading-font text-3xl sm:text-4xl font-medium text-[#111111]">{layer.title}</h1>
          </motion.div>

          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-14">
            <div className="prose prose-lg max-w-none text-[#111111] leading-relaxed">
              {layer.introduction?.split('\n\n').map((p, i) => (
                <p key={i} className="mb-5">{p}</p>
              ))}
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-14">
            <div className="prose prose-lg max-w-none text-[#111111] leading-relaxed">
              {layer.deep_text?.split('\n\n').map((p, i) => (
                <p key={i} className="mb-5">{p}</p>
              ))}
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-14">
            <div className="bg-[#FDF9F7] rounded-2xl p-8 text-center">
              <p className="heading-font text-xl text-[#111111] italic leading-relaxed">
                "{layer.affirmation}"
              </p>
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="mb-14" data-testid="reflections-section">
            <h2 className="heading-font text-2xl font-medium text-[#111111] mb-3">Reflektionsfrågor</h2>
            <p className="text-[#111111]/50 mb-8">Ta dig tid med dessa frågor. Skriv det som kommer till dig – det finns inga rätta svar.</p>
            
            <div className="space-y-6">
              {layer.reflection_questions?.map((q, i) => (
                <div key={i} className="bg-white border border-[#EBE3DF] rounded-2xl p-6" data-testid={`reflection-${i}`}>
                  <p className="text-[#111111] font-medium mb-4">{q}</p>
                  <Textarea
                    placeholder="Skriv din reflektion här..."
                    value={reflections[i] || ''}
                    onChange={(e) => setReflections(prev => ({ ...prev, [i]: e.target.value }))}
                    rows={4}
                    className="rounded-xl mb-3 border-[#EBE3DF] focus:border-[#A0655A] resize-none"
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={() => saveReflection(i)}
                      disabled={savingIdx === i || !reflections[i]?.trim()}
                      variant="ghost"
                      size="sm"
                      className="text-[#A0655A] hover:text-[#8B5549] hover:bg-[#FBF5F2]"
                      data-testid={`save-reflection-${i}`}
                    >
                      {savingIdx === i ? 'Sparar...' : 'Spara'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-14">
            <h2 className="heading-font text-2xl font-medium text-[#111111] mb-6">Övning</h2>
            {layer.exercise && (
              <div className="bg-white border border-[#EBE3DF] rounded-2xl p-6">
                <h3 className="font-medium text-[#111111] text-lg mb-3">{layer.exercise.title}</h3>
                <p className="text-[#111111] mb-5 leading-relaxed">{layer.exercise.description}</p>
                {layer.exercise.instructions && (
                  <div className="bg-[#FDF9F7] rounded-xl p-5">
                    <ol className="space-y-2 text-sm text-[#111111]">
                      {layer.exercise.instructions.map((inst, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <span className="text-[#A0655A] font-medium flex-shrink-0">{j + 1}.</span>
                          <span>{inst}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            )}
          </motion.section>

          {/* Recommended guided exercise */}
          {RECOMMENDED_EXERCISES[id] && (
            <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32 }} className="mb-14" data-testid="recommended-exercise">
              <div className="bg-[#F9F3EF] border border-[#EBE3DF]/60 rounded-2xl p-6 sm:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#A0655A]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Wind className="text-[#A0655A]" size={18} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-[#A0655A] font-medium uppercase tracking-wide mb-1">Rekommenderad övning</p>
                    <h3 className="font-medium text-[#111111] text-lg mb-2">{RECOMMENDED_EXERCISES[id].title}</h3>
                    <p className="text-sm text-[#111111]/60 leading-relaxed mb-4">{RECOMMENDED_EXERCISES[id].why}</p>
                    <Button
                      onClick={() => navigate(`/ovningar?open=${RECOMMENDED_EXERCISES[id].id}`)}
                      variant="outline"
                      size="sm"
                      className="rounded-full border-[#A0655A] text-[#A0655A] hover:bg-[#FBF5F2]"
                      data-testid="recommended-exercise-btn"
                    >
                      Gör övningen <ChevronRight size={14} className="ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.section>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="border-t border-[#EBE3DF] pt-10">
            {/* Navigation between steps — always visible */}
            <div className="flex flex-wrap gap-3 justify-center mb-6">
              {prevLayer && (
                <Button onClick={() => navigate(`/steg/${prevLayer}`)} variant="outline" className="rounded-full" data-testid="prev-step-btn">
                  <ChevronLeft size={16} className="mr-1" /> Föregående steg
                </Button>
              )}
              <Button onClick={() => navigate('/dashboard')} variant="outline" className="rounded-full" data-testid="back-to-dashboard-btn">
                Alla steg
              </Button>
              {nextLayer && (
                <Button onClick={() => navigate(`/steg/${nextLayer}`)} variant="outline" className="rounded-full" data-testid="next-step-btn">
                  Nästa steg <ChevronRight size={16} className="ml-1" />
                </Button>
              )}
            </div>

            {/* Complete step button */}
            {!layer.is_completed && (
              <div className="text-center">
                <p className="text-[#111111]/50 mb-4 text-sm">När du känner att du är redo, markera steget som slutfört.</p>
                <Button 
                  onClick={handleComplete}
                  disabled={completing}
                  className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-8 py-6 text-base"
                  data-testid="complete-phase-btn"
                >
                  {completing ? 'Sparar...' : 'Markera som slutfört'}
                  <ChevronRight className="ml-2" size={18} />
                </Button>
              </div>
            )}
            {layer.is_completed && (
              <p className="text-center text-[#A0655A] text-sm font-medium">Du har slutfört detta steg</p>
            )}
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
